import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { authFetch } from '../utils/authFetch';
import { AllocationRows } from '../nc/CostAllocation';

// Ignore asynchronous UI labels/stock hints; retain every accounting input.
export function allocationFingerprint(doc) {
  const fields = ['id', 'tip_document', 'doc_no', 'doc_serie', 'doc_date', 'contact_id', 'cont_id',
    'branch_id', 'bxsoldint', 'e_tva_inc', 'bxvaluta_id', 'bxvaluta_curs', 'bxcurs_invoice', 'tip_factura_iesire_id'];
  const lineFields = ['id', 'uid', 'bxpart_id', 'bxgestiune_id', 'qty', 'price', 'vat_rate',
    'cont_venit_id', 'cont_tva_id', 'cont_client_id', 'bxstocin_id', 'alloc', 'storno_source_line_id', 'serii'];
  return JSON.stringify([fields.map(k => k === 'e_tva_inc' ? !!doc[k] : doc[k]), (doc.linii || []).map(l => lineFields.map(k => l[k]))]);
}

export function allocationError(groups) {
  for (const g of groups) {
    if (Number(g.base) === 0 && !g.allocations?.length) continue;
    const rows = g.allocations || [];
    const percent = rows.length > 0 && rows.every(a => a.percent != null);
    const target = percent ? 100 : Number(g.base);
    const total = rows.reduce((s, a) => s + Number(percent ? a.percent : a.amount), 0);
    if (!rows.length || rows.some(a => !Number(a.cost_center_id)) ||
        new Set(rows.map(a => Number(a.cost_center_id))).size !== rows.length ||
        rows.some(a => !Number.isFinite(Number(percent ? a.percent : a.amount)) ||
          (percent ? Number(a.percent) < 0 : Number(a.amount) * Number(g.base) < 0)) ||
        !Number.isFinite(total) || Math.abs(total - target) > 0.000001) {
      return `Completați repartizarea integrală pentru linia ${g.line_index + 1}, contul ${g.code}, înainte de salvare.`;
    }
  }
  return '';
}

const InvoiceCostAllocation = forwardRef(function InvoiceCostAllocation({ doc, onChange, disabled,
  endpoint = '/iesire/cost-allocation-preview', fingerprintOf = allocationFingerprint,
  ready = d => d.contact_id && d.cont_id && d.linii?.length && d.linii.every(l => l.bxpart_id && Number(l.qty)),
  lineKey = l => l?.id || l?.uid, previewWhenDisabled = false }, ref) {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const fingerprint = fingerprintOf(doc);
  const current = useRef(fingerprint);
  current.current = fingerprint;
  useEffect(() => {
    let alive = true;
    authFetch('/repartizari-centre/config').then(c => { if (alive) setConfig(c); })
      .catch(e => { if (alive) setError(`Nu s-a putut verifica opțiunea de repartizare: ${e.message}`); });
    return () => { alive = false; };
  }, []);
  const rowsPayload = groups => groups.map(g => ({ line_index: g.line_index, account_id: g.account_id,
    allocations: (g.allocations || []).map(a => ({ cost_center_id: Number(a.cost_center_id),
      percent: a.percent == null ? null : Number(a.percent), amount: a.percent == null ? Number(a.amount) : null })) }));
  const identity = (candidate, g) => {
    const line = candidate.linii?.[g.line_index];
    return `${lineKey(line) || g.line_index}:${g.account_id}`;
  };
  const calculate = async (candidate) => {
    if (inFlight.current) throw new Error('Așteptați calcularea repartizării.');
    inFlight.current = true; setLoading(true); setError('');
    const signature = fingerprintOf(candidate);
    try {
      const result = await authFetch(`${endpoint}${candidate.id ? `?doc_id=${candidate.id}` : ''}`, {
        method: 'POST', body: JSON.stringify({ ...candidate, auto_number: !!candidate.deviz_id || !!candidate.san_situatie_id,
          e_tva_inc: candidate.e_tva_inc ? 1 : 0 }) });
      if (current.current !== signature) throw new Error('Factura s-a modificat în timpul calculului. Recalculați repartizarea.');
      const previous = new Map((state?.groups || []).map(g => [g.identity, g.allocations]));
      const groups = result.groups.map(g => ({ ...g, identity: identity(candidate, g),
        allocations: previous.get(identity(candidate, g)) ?? g.allocations ?? [] }));
      setState({ signature, groups });
      onChange(rowsPayload(groups), { automatic: true });
      return groups;
    } catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); inFlight.current = false; }
  };
  useEffect(() => {
    if (!config?.enabled || (disabled && !previewWhenDisabled) || !ready(doc)) return;
    const timer = setTimeout(() => { calculate(doc).catch(() => {}); }, 600);
    return () => clearTimeout(timer);
    // The fingerprint covers accounting changes; allocation edits do not trigger a recalculation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, config?.enabled, disabled]);
  useImperativeHandle(ref, () => ({ prepare: async candidate => {
    if (!config) throw new Error(error || 'Așteptați verificarea opțiunii de repartizare pe centre.');
    if (!config.enabled) return undefined;
    const groups = state?.signature === fingerprintOf(candidate) ? state.groups : await calculate(candidate);
    const message = allocationError(groups);
    if (message) { setError(message); throw new Error(message); }
    setError('');
    return rowsPayload(groups);
  } }));
  if (!config && !error) return null;
  if (config && !config.enabled) return null;
  return <Card className="my-3"><Card.Body>
    <h5>Repartizare pe centre</h5>
    <p>Repartizați integral fiecare venit și cheltuială înainte de salvarea facturii. Repartizarea se salvează împreună cu factura.</p>
    {error && <Alert variant="danger">{error}</Alert>}
    {config?.enabled && <>
      <Button type="button" variant="outline-primary" disabled={disabled || loading || !doc.contact_id || !doc.linii?.length}
        onClick={() => calculate(doc).catch(() => {})}>{loading ? 'Se calculează…' : 'Recalculează bazele'}</Button>
      {state && state.signature !== fingerprint && <Alert variant="warning" className="mt-2">Factura s-a modificat. Recalculați bazele de repartizare.</Alert>}
      {state?.groups.map((g, index) => <div className="mt-3" key={g.identity}>
        <strong>Linia {g.line_index + 1} · {g.code} — {g.name} · {Number(g.base).toLocaleString('ro-RO')} lei</strong>
        <AllocationRows base={g.base} centers={config.centers} value={g.allocations}
          disabled={disabled || loading || state.signature !== fingerprint}
          onChange={allocations => {
            const groups = state.groups.map((row, i) => i === index ? { ...row, allocations } : row);
            setState({ ...state, groups }); setError(''); onChange(rowsPayload(groups));
          }} />
      </div>)}
      {state && !state.groups.length && <p className="mt-2">Documentul nu generează baze de cheltuieli sau venituri de repartizat.</p>}
    </>}
  </Card.Body></Card>;
});
export default InvoiceCostAllocation;
